import type { SessionView } from '../api/client';
import { type Key, pick, type T } from '../i18n';
import { trustHref } from '../page';

type Props = {
  branding: SessionView['branding'];
  progress?: SessionView['progress'];
  locale: string;
  t: T;
  onPause?: () => void;
  /** Links the survey's trust page; shown on every participant screen (IDEA.md: Trust). */
  trustSlug?: string;
  /** A short line beside the trust link, e.g. the pulse round ("Omgång 4 · oktober"). */
  context?: string;
};

/** The progress in words: the precision of a percentage is not real (ADR 0002, Progress). */
export function progressWords(value: number): Key {
  if (value >= 1) return 'progressDone';
  if (value < 0.15) return 'progressStart';
  if (value < 0.4) return 'progressEarly';
  if (value < 0.6) return 'progressHalf';
  if (value < 0.85) return 'progressPastHalf';
  return 'progressNearly';
}

/** Brand, a continuous progress bar with the current part's title, the trust link and the pause control (IDEA.md: the participant is in control). */
export function Header({ branding, progress, locale, t, onPause, trustSlug, context }: Props) {
  return (
    <header class="header">
      <div class="header-row">
        <div class="brand">
          <Logo branding={branding} />
          {!(branding.logo && branding.logoIncludesName) && <span class="brand-name">{branding.displayName}</span>}
        </div>
        <div class="header-actions">
          {context && <span class="header-context">{context}</span>}
          {trustSlug && (
            <a class="header-link" href={trustHref(trustSlug, locale)}>
              {t('trustShort')}
            </a>
          )}
          {onPause && (
            <button type="button" class="button-secondary" onClick={onPause}>
              {t('pause')}
            </button>
          )}
        </div>
      </div>
      {progress && (
        <div class="progress">
          {progress.part && <p class="progress-text">{pick(progress.part, locale)}</p>}
          <progress max={1} value={progress.value} aria-label={t('progressName')} aria-valuetext={t(progressWords(progress.value))} />
          {/* Read out as the bar's value; shown for everyone. */}
          <p class="progress-text" aria-hidden="true">
            {t(progressWords(progress.value))}
          </p>
        </div>
      )}
    </header>
  );
}

/**
 * The organisation's logo. A wordmark (`logoIncludesName`) carries the name as its text alternative;
 * otherwise the name is printed beside it and the logo is decorative. In dark mode a `logoDark`
 * variant is used, or the logo gets a light plate (styles.css).
 */
function Logo({ branding }: { branding: SessionView['branding'] }) {
  if (!branding.logo) return <span class="brand-mark" aria-hidden="true" />;
  const alt = branding.logoIncludesName ? branding.displayName : '';
  return (
    <picture>
      {branding.logoDark && <source srcset={branding.logoDark} media="(prefers-color-scheme: dark)" />}
      <img src={branding.logo} alt={alt} class={`brand-logo${branding.logoDark ? '' : ' on-plate'}`} />
    </picture>
  );
}
